/**
 * Client-side utilities for managing FCM push tokens (W4M-97 FCM migration).
 *
 * Replaces the raw Web Push subscription flow: tokens are minted via Firebase
 * Cloud Messaging (`getToken`) against our Firebase VAPID key, then synced to
 * the /api/fcm-token endpoint with retry + backoff.
 *
 * `firebase/messaging` (and the app bootstrap it needs) are loaded via dynamic
 * import so the FCM SDK stays out of the main bundle until a push code path
 * actually runs.
 */

import { clientLogger } from "@/lib/client-logger";
import type { Messaging } from "firebase/messaging";

const SYNC_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

/**
 * Set after a successful token registration. Lets `reconcilePushPermission`
 * distinguish "user revoked permission after enrolling" (cleanup needed) from
 * "never enrolled on this device" (nothing to do).
 */
const REGISTERED_MARKER_KEY = "w4m:push:registered";

// ---------------------------------------------------------------------------
// Permission / subscription state (unchanged semantics)
// ---------------------------------------------------------------------------

/**
 * Get the current push notification permission state.
 */
export function getPushPermissionState():
  | "granted"
  | "denied"
  | "default"
  | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission as "granted" | "denied" | "default";
}

/**
 * Check if the current browser has an active push subscription.
 */
export async function isPushSubscribed(): Promise<boolean> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}

// ---------------------------------------------------------------------------
// subscribeToPush
// ---------------------------------------------------------------------------

/**
 * Enroll the current browser for FCM push notifications.
 *
 * Requests permission if needed, removes any legacy web-push subscription
 * minted against the old (non-Firebase) VAPID key, mints an FCM token bound
 * to our service worker registration, and syncs it to the server.
 *
 * Resolves to the FCM token string on success; `null` when push is
 * unsupported, the user denied permission, or the server sync ultimately
 * failed (in which case the local token is deleted so we don't end up in a
 * ghost state).
 */
export async function subscribeToPush(): Promise<string | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  const fcm = await loadMessaging();
  if (!fcm) return null;

  if (!("Notification" in window)) return null;
  await Notification.requestPermission();
  // Re-read instead of trusting the resolved value — some WebKit builds
  // resolve the legacy callback signature with `undefined`.
  if (Notification.permission !== "granted") {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  await removeLegacySubscription(registration);

  let token: string;
  try {
    token = await fcm.getToken(fcm.messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (err) {
    clientLogger.error("[push] FCM getToken failed", { err });
    return null;
  }
  if (!token) return null;

  const synced = await syncTokenToServer(token);
  if (!synced) {
    // Server never recorded the token — delete it locally so the browser
    // doesn't think it's enrolled while the backend has no record.
    await fcm.deleteToken(fcm.messaging).catch((err: unknown) => {
      clientLogger.warn("[push] deleteToken after failed sync errored", {
        err,
      });
    });
    return null;
  }

  setRegisteredMarker();
  return token;
}

// ---------------------------------------------------------------------------
// unsubscribeFromPush
// ---------------------------------------------------------------------------

/**
 * Unsubscribe from push notifications on the current browser.
 * Deletes the local FCM token, tells the server to drop it, and clears the
 * registered marker. Resolves `true` once the local token is gone.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }

  const fcm = await loadMessaging();
  if (!fcm) return false;

  const registration = await navigator.serviceWorker.ready;

  // Grab the current token first so the server DELETE can target it.
  let token: string | null = null;
  try {
    token = await fcm.getToken(fcm.messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (err) {
    clientLogger.warn("[push] getToken during unsubscribe failed", { err });
  }

  try {
    await fcm.deleteToken(fcm.messaging);
  } catch (err) {
    clientLogger.error("[push] deleteToken failed", { err });
    return false;
  }

  await deleteTokenFromServer(
    token ? { token } : { userAgent: navigator.userAgent },
  );
  clearRegisteredMarker();
  return true;
}

// ---------------------------------------------------------------------------
// refreshPushToken (replaces refreshSubscription)
// ---------------------------------------------------------------------------

/**
 * Re-sync the current device's FCM token to the server. Used by:
 *
 *  - the SW registration hook on every load (handles token rotation across
 *    browser restarts)
 *  - the visibilitychange refresh in `sw-register` (catches mid-session
 *    rotations)
 *
 * Strictly non-interactive: NEVER prompts for permission, and treats the
 * absence of a local push subscription as an opted-out signal (no re-enroll).
 * Resolves `false` when unsupported, permission isn't granted, there is no
 * local subscription, or the sync ultimately failed; `true` otherwise.
 */
export async function refreshPushToken(): Promise<boolean> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }

  const fcm = await loadMessaging();
  if (!fcm) return false;

  if (getPushPermissionState() !== "granted") return false;

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return false;

  await removeLegacySubscription(registration);

  let token: string;
  try {
    token = await fcm.getToken(fcm.messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (err) {
    clientLogger.error("[push] FCM getToken during refresh failed", { err });
    return false;
  }
  if (!token) return false;

  const synced = await syncTokenToServer(token);
  if (synced) setRegisteredMarker();
  return synced;
}

// ---------------------------------------------------------------------------
// reconcilePushPermission — permission-drift cleanup
// ---------------------------------------------------------------------------

/**
 * Detect "user revoked notification permission in browser settings after we
 * registered a token" and clean up: delete the local FCM token, tell the
 * server to drop this device, and clear the registered marker.
 * No-op when permission is still granted or we never registered.
 */
export async function reconcilePushPermission(): Promise<void> {
  if (typeof window === "undefined") return;
  if (getPushPermissionState() === "granted") return;

  let marker: string | null = null;
  try {
    marker = window.localStorage.getItem(REGISTERED_MARKER_KEY);
  } catch {
    return; // localStorage unavailable (private mode) — nothing to reconcile
  }
  if (marker !== "1") return;

  const fcm = await loadMessaging();
  if (fcm) {
    try {
      await fcm.deleteToken(fcm.messaging);
    } catch (err) {
      // Expected on some browsers once permission is revoked — best effort.
      clientLogger.warn("[push] deleteToken during reconcile failed", { err });
    }
  }

  await deleteTokenFromServer({ userAgent: navigator.userAgent });
  clearRegisteredMarker();
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface MessagingHandle {
  messaging: Messaging;
  getToken: typeof import("firebase/messaging").getToken;
  deleteToken: typeof import("firebase/messaging").deleteToken;
}

/**
 * Dynamically load firebase/messaging and create the Messaging instance.
 * Returns `null` when FCM isn't supported in this browser. The Firebase app
 * is only initialized when support is confirmed.
 */
async function loadMessaging(): Promise<MessagingHandle | null> {
  let mod: typeof import("firebase/messaging");
  try {
    mod = await import("firebase/messaging");
  } catch (err) {
    clientLogger.error("[push] failed to load firebase/messaging", { err });
    return null;
  }

  const supported = await mod.isSupported().catch(() => false);
  if (!supported) return null;

  const { getFirebaseApp } = await import("@/lib/firebase/client");
  return {
    messaging: mod.getMessaging(getFirebaseApp()),
    getToken: mod.getToken,
    deleteToken: mod.deleteToken,
  };
}

/**
 * Legacy-VAPID shim: a push subscription minted by the pre-FCM web-push flow
 * was keyed on the old VAPID key. FCM's `getToken` cannot reuse it (the
 * applicationServerKey doesn't match), so it must be unsubscribed BEFORE
 * getToken mints a fresh FCM-keyed subscription. Subscriptions already keyed
 * on the Firebase VAPID key are left alone.
 */
async function removeLegacySubscription(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;
  if (!vapidKey) return;

  try {
    const existing = await registration.pushManager.getSubscription();
    if (!existing) return;

    const currentKey = existing.options?.applicationServerKey ?? null;
    if (keyMatchesVapid(currentKey, vapidKey)) return;

    await existing.unsubscribe();
    clientLogger.info("[push] removed legacy web-push subscription", {
      endpoint: existing.endpoint,
    });
  } catch (err) {
    clientLogger.warn("[push] legacy subscription shim failed", { err });
  }
}

function keyMatchesVapid(
  key: ArrayBuffer | null,
  vapidKey: string,
): boolean {
  if (!key) return false;
  const expected = urlBase64ToUint8Array(vapidKey);
  const actual = new Uint8Array(key);
  if (actual.byteLength !== expected.byteLength) return false;
  for (let i = 0; i < expected.byteLength; i++) {
    if (actual[i] !== expected[i]) return false;
  }
  return true;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

/**
 * POST the FCM token to the server with retry + backoff (1s → 2s → 4s).
 * Resolves `true` on success, `false` once retries are exhausted or the
 * server rejects with a 4xx (no retry — the token is bad).
 */
function syncTokenToServer(token: string): Promise<boolean> {
  return fetchWithRetry("/api/fcm-token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, userAgent: navigator.userAgent }),
  });
}

/** Best-effort DELETE of this device's token record on the server. */
function deleteTokenFromServer(payload: {
  token?: string;
  userAgent?: string;
}): Promise<boolean> {
  return fetchWithRetry("/api/fcm-token", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

async function fetchWithRetry(
  input: string,
  init: RequestInit,
): Promise<boolean> {
  for (let attempt = 0; attempt <= SYNC_RETRY_DELAYS_MS.length; attempt++) {
    try {
      const response = await fetch(input, init);
      if (response.ok) return true;

      // 4xx is the server telling us this token is bad (validation failure,
      // auth, etc). Don't retry; let the caller drop it.
      if (response.status >= 400 && response.status < 500) {
        clientLogger.error("[push] token sync rejected by server", {
          status: response.status,
          method: init.method,
        });
        return false;
      }

      // 5xx — fall through to the backoff below.
      if (attempt === SYNC_RETRY_DELAYS_MS.length) {
        clientLogger.error("[push] token sync failed after retries", {
          status: response.status,
          method: init.method,
        });
        return false;
      }
    } catch (err) {
      // Network error — retry if we have budget.
      if (attempt === SYNC_RETRY_DELAYS_MS.length) {
        clientLogger.error("[push] token sync network error after retries", {
          err,
          method: init.method,
        });
        return false;
      }
    }

    await sleep(SYNC_RETRY_DELAYS_MS[attempt]);
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function setRegisteredMarker(): void {
  try {
    window.localStorage.setItem(REGISTERED_MARKER_KEY, "1");
  } catch {
    // localStorage unavailable (private mode) — marker is best-effort.
  }
}

function clearRegisteredMarker(): void {
  try {
    window.localStorage.removeItem(REGISTERED_MARKER_KEY);
  } catch {
    // ignore
  }
}
